import type {
  FolderApprovalProgressDto,
  FolderResponseDto,
  ListFoldersResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { useQuery } from '@tanstack/react-query';
import { projectFoldersQuery } from '../api/project-folder.api';

const TOP_LEVEL_PAGE_SIZE = 100;

type ApprovalStatus = FolderApprovalProgressDto['status'];

interface ProjectApprovalAggregate {
  approvedCount: number;
  totalCount: number;
  /**
   * Sum of `commentsCount` across all top-level folders in the project. The
   * backend's `commentsCount` is per-folder and already
   * counts comments on files within the folder's subtree, so summing across
   * top-level folders gives us the project-wide total without N additional
   * queries.
   *
   * Caveats:
   *   - Does NOT include comments on briefs (those use a separate target
   *     type and have no aggregated endpoint). The Snapshot metric is
   *     intentionally "file comments only" until a project-scoped comments
   *     count endpoint exists.
   *   - Subject to the same `isTruncated` ceiling as the approval counts.
   */
  commentsCount: number;
  /**
   * Derived from the summed counts, not a `status` field on any single folder
   * (server's per-folder `status` is meaningless when aggregated). `to_review`
   * means nothing approved yet; `in_review` means partial; `approved` means all.
   */
  status: ApprovalStatus;
  /**
   * True when the project has more top-level folders than `pageSize=100`. The
   * aggregate only sums the first page, so the pill silently undercounts. Today
   * This is an accepted ceiling; surface so callers can warn /
   * disable the pill if product ever needs accuracy at scale.
   */
  isTruncated: boolean;
}

function deriveStatus(approvedCount: number, totalCount: number): ApprovalStatus {
  if (totalCount === 0 || approvedCount === 0) return 'to_review';
  if (approvedCount === totalCount) return 'approved';
  return 'in_review';
}

function sumApprovalProgress(data: ListFoldersResponseDto): ProjectApprovalAggregate {
  const summed = data.items.reduce(
    (acc, folder: FolderResponseDto) => ({
      approvedCount: acc.approvedCount + folder.approvalProgress.approvedCount,
      totalCount: acc.totalCount + folder.approvalProgress.totalCount,
      commentsCount: acc.commentsCount + folder.commentsCount,
    }),
    { approvedCount: 0, totalCount: 0, commentsCount: 0 },
  );

  return {
    ...summed,
    status: deriveStatus(summed.approvedCount, summed.totalCount),
    isTruncated: data.total > data.items.length,
  };
}

export function useProjectApprovalAggregate(projectId: string | undefined) {
  const { data, isLoading } = useQuery({
    ...projectFoldersQuery(projectId, { pageSize: TOP_LEVEL_PAGE_SIZE }),
    select: sumApprovalProgress,
  });

  if (import.meta.env.DEV && data?.isTruncated) {
    // eslint-disable-next-line no-console
    console.warn(
      `[useProjectApprovalAggregate] Project has more than ${TOP_LEVEL_PAGE_SIZE} top-level folders; aggregate undercounts. Re-raise if product needs server-side aggregation.`,
    );
  }

  return {
    approvedCount: data?.approvedCount ?? 0,
    totalCount: data?.totalCount ?? 0,
    commentsCount: data?.commentsCount ?? 0,
    status: data?.status ?? 'to_review',
    isTruncated: data?.isTruncated ?? false,
    isLoading,
  };
}
